import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from "react";
import {
    Box,
    Container,
    Typography,
    Paper,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Card,
    Divider,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Snackbar,
    ChipProps,
} from "@mui/material";
import {
    LocalShipping,
    DragIndicator,
    Download,
    Person,
    LocationOn,
    ShoppingBag,
    Delete,
    DeleteForever,
    InfoOutlined,
    Home,
    Business,
    DeleteOutline,
    MoveUp,
    RestoreFromTrash,
    AddCircleOutline,
    RestartAlt,
    CheckCircle,
    Phone,
    ArrowDropDown,
} from "@mui/icons-material";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { orderService } from "../../services/orderService";
import { Order } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";
import html2canvas from "html2canvas";
import { OrderDetailDrawer } from "../../components/orders/OrderDetailDrawer";
import { useOrderActions } from "../../components/orders/useOrderActions";
import { CourierPicker } from "../../components/orders/quickEdit/CourierPicker";
import { getStatusColor, getStatusIcon } from "../../components/orders/OrderStatusManager";

// ─── Types ───────────────────────────────────────────────────────────────────

interface DeliveryOrder {
    order_id: string;
    order_number: string;
    customer_name: string;
    customer_phone: string;
    shipping_address: string;
    items_count: number;
    total_amount: number;
    // What's still to collect. Missing on cards saved before it was added,
    // until the board refreshes.
    amount_due?: number | null;
    status: Order["status"];
    priority: number;
    delivery_service_type?: string | null;
    // Missing on cards saved before these were added, until the board refreshes.
    delivery_service_id?: string | null;
    delivery_service_name?: string | null;
    consignment_id?: string | null;
}

type SectionKey = "own" | "home" | "point" | "trash";

interface SectionsState {
    own: DeliveryOrder[];
    home: DeliveryOrder[];
    point: DeliveryOrder[];
    trash: DeliveryOrder[];
}

interface SectionConfig {
    key: SectionKey;
    label: string;
    icon: React.ReactNode;
    color: string;
    emptyMessage: string;
    emptySubMessage: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STORAGE_KEY = "delivery_planner_sections";

const SECTION_CONFIGS: SectionConfig[] = [
    {
        key: "own",
        label: "Own Deliveries",
        icon: <LocalShipping />,
        color: "primary.main",
        emptyMessage: "No orders in Own Deliveries",
        emptySubMessage: "Move orders here or refresh to load new orders",
    },
    {
        key: "home",
        label: "Home Deliveries (Courier)",
        icon: <Home />,
        color: "#e65100",
        emptyMessage: "No orders in Home Deliveries",
        emptySubMessage: "Move orders here for courier home delivery",
    },
    {
        key: "point",
        label: "Point Deliveries (Courier)",
        icon: <Business />,
        color: "#1565c0",
        emptyMessage: "No orders in Point Deliveries",
        emptySubMessage: "Move orders here for courier office pickup",
    },
    {
        key: "trash",
        label: "Trash",
        icon: <DeleteOutline />,
        color: "text.disabled",
        emptyMessage: "Trash is empty",
        emptySubMessage: "Deleted orders will appear here for recovery",
    },
];

const MOVE_TARGETS: Record<
    SectionKey,
    { key: SectionKey; label: string; icon: React.ReactNode }[]
> = {
    own: [
        {
            key: "home",
            label: "Home Deliveries",
            icon: <Home fontSize="small" />,
        },
        {
            key: "point",
            label: "Point Deliveries",
            icon: <Business fontSize="small" />,
        },
        {
            key: "trash",
            label: "Trash",
            icon: <DeleteOutline fontSize="small" />,
        },
    ],
    home: [
        {
            key: "own",
            label: "Own Deliveries",
            icon: <LocalShipping fontSize="small" />,
        },
        {
            key: "point",
            label: "Point Deliveries",
            icon: <Business fontSize="small" />,
        },
        {
            key: "trash",
            label: "Trash",
            icon: <DeleteOutline fontSize="small" />,
        },
    ],
    point: [
        {
            key: "own",
            label: "Own Deliveries",
            icon: <LocalShipping fontSize="small" />,
        },
        {
            key: "home",
            label: "Home Deliveries",
            icon: <Home fontSize="small" />,
        },
        {
            key: "trash",
            label: "Trash",
            icon: <DeleteOutline fontSize="small" />,
        },
    ],
    trash: [
        {
            key: "own",
            label: "Own Deliveries",
            icon: <RestoreFromTrash fontSize="small" />,
        },
        {
            key: "home",
            label: "Home Deliveries",
            icon: <Home fontSize="small" />,
        },
        {
            key: "point",
            label: "Point Deliveries",
            icon: <Business fontSize="small" />,
        },
    ],
};

const DEFAULT_SECTIONS: SectionsState = {
    own: [],
    home: [],
    point: [],
    trash: [],
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const reindex = (orders: DeliveryOrder[]): DeliveryOrder[] =>
    orders.map((o, i) => ({ ...o, priority: i + 1 }));

/** The list sends `amount_due`; a single order has its payments instead —
 * same sum as the order drawer and the API (bill − payments, never below 0). */
const amountDueOf = (order: any): number | null => {
    if (order.amount_due != null) return Number(order.amount_due);
    if (!Array.isArray(order.payment_transactions)) return null;
    const paid = order.payment_transactions.reduce(
        (sum: number, t: any) => sum + Number(t.amount || 0),
        0,
    );
    return Math.max(0, Math.round((Number(order.total_amount) - paid) * 100) / 100);
};

/** A card's due amount; an old saved card without one counts its full bill. */
const dueOrTotal = (o: DeliveryOrder) => o.amount_due ?? o.total_amount;

const SHORT_SECTION_LABEL: Record<SectionKey, string> = {
    own: "Own Deliveries",
    home: "Home Deliveries",
    point: "Point Deliveries",
    trash: "Trash",
};

const isCourier = (type?: string | null) => type === "THIRD_PARTY";

/**
 * Where a card belongs after its order changes. Only a switch between our
 * own riders and a courier company moves it — picking a courier sends it
 * from Own to Home, picking one of our riders brings it back from Home or
 * Point. Anything else (or Trash) stays put, so cards placed by hand aren't
 * shuffled on a refresh.
 */
const sectionAfterChange = (
    from: SectionKey,
    before: DeliveryOrder,
    after: DeliveryOrder,
): SectionKey => {
    // Very old saved cards don't know their type yet.
    if (from === "trash" || before.delivery_service_type === undefined) return from;
    const wasCourier = isCourier(before.delivery_service_type);
    const nowCourier = isCourier(after.delivery_service_type);
    if (wasCourier === nowCourier) return from;
    if (nowCourier && from === "own") return "home";
    if (!nowCourier && (from === "home" || from === "point")) return "own";
    return from;
};

const mapOrderToDeliveryOrder = (order: any, index: number): DeliveryOrder => ({
    order_id: order.order_id,
    order_number: order.order_number,
    customer_name:
        order.customer_name ||
        (order.customer
            ? `${order.customer.first_name} ${order.customer.last_name || ""}`.trim()
            : "Walk-in Customer"),
    customer_phone: order.customer_phone || order.customer?.phone || "",
    shipping_address: order.shipping_address,
    items_count: order.item_count || order.items?.length || 0,
    total_amount: order.total_amount,
    amount_due: amountDueOf(order),
    status: order.status,
    priority: index + 1,
    delivery_service_type:
        order.delivery_service_type ?? order.delivery_service?.type ?? null,
    delivery_service_id:
        order.delivery_service?.delivery_service_id ?? order.delivery_service_id ?? null,
    delivery_service_name:
        order.delivery_service?.name ?? order.delivery_service_name ?? null,
    consignment_id: order.consignment_id ?? null,
});

/** Like the drawer's notify, plus which card it's about (for "moved to …"). */
type PlannerNotify = (message: string, severity?: "success" | "error", orderId?: string) => void;

// The courier chip and its menu/dialog (which render elsewhere in the page
// but still bubble through the card) must not start a card drag.
const stopDragStart = (e: React.SyntheticEvent) => e.stopPropagation();

/** The card's current courier. An older API sends the name without the id;
 * the name alone still shows (the menu just can't tick the current one). */
const cardCourier = (o: DeliveryOrder) =>
    o.delivery_service_id || o.delivery_service_name
        ? { id: o.delivery_service_id ?? "", name: o.delivery_service_name || "Delivery service" }
        : null;

const SECTION_KEYS: SectionKey[] = ["own", "home", "point", "trash"];

/** Shipped, delivered and cancelled orders are done here — they leave the planner. */
const isFinished = (status: Order["status"]) =>
    status === "SHIPPED" || status === "DELIVERED" || status === "CANCELLED";

const boardIds = (sections: SectionsState): Set<string> =>
    new Set(SECTION_KEYS.flatMap((key) => sections[key].map((o) => o.order_id)));

/**
 * Refresh cards from fresh order data, keeping each card in its section and
 * position. A `null` entry means the order no longer exists; those cards and
 * finished orders are removed. Cards without an entry are left as they are.
 */
const applyCardUpdates = (
    sections: SectionsState,
    updates: Map<string, DeliveryOrder | null>,
): SectionsState => {
    let changed = false;
    const next = { ...sections };
    // Cards whose courier kind changed join the end of their new section.
    const arrivals: SectionsState = { own: [], home: [], point: [], trash: [] };
    for (const key of SECTION_KEYS) {
        if (!sections[key].some((card) => updates.has(card.order_id))) continue;
        changed = true;
        next[key] = sections[key].flatMap((card) => {
            if (!updates.has(card.order_id)) return [card];
            const fresh = updates.get(card.order_id);
            if (!fresh || isFinished(fresh.status)) return [];
            const target = sectionAfterChange(key, card, fresh);
            if (target === key) return [fresh];
            arrivals[target].push(fresh);
            return [];
        });
    }
    if (!changed) return sections;
    for (const key of SECTION_KEYS) {
        next[key] = reindex([...next[key], ...arrivals[key]]);
    }
    return next;
};

/** New orders join Own or Home (courier) Deliveries by their delivery service. */
const addNewCards = (
    sections: SectionsState,
    cards: DeliveryOrder[],
): SectionsState => ({
    ...sections,
    own: reindex([
        ...sections.own,
        ...cards.filter((o) => o.delivery_service_type !== "THIRD_PARTY"),
    ]),
    home: reindex([
        ...sections.home,
        ...cards.filter((o) => o.delivery_service_type === "THIRD_PARTY"),
    ]),
});

// Cards that dropped out of the Pending/Confirmed list are re-fetched one
// by one, this many at a time.
const REFRESH_BATCH_SIZE = 5;

const saveToLocalStorage = (sections: SectionsState) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
    } catch (err) {
        console.error("Failed to save to localStorage:", err);
    }
};

const loadFromLocalStorage = (): SectionsState | null => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.own && parsed.home && parsed.point && parsed.trash) {
                return parsed;
            }
        }
    } catch (err) {
        console.error("Failed to load from localStorage:", err);
    }
    // Migrate old single-list format
    try {
        const oldStored = localStorage.getItem("delivery_planner_orders");
        if (oldStored) {
            const oldOrders = JSON.parse(oldStored);
            if (Array.isArray(oldOrders) && oldOrders.length > 0) {
                localStorage.removeItem("delivery_planner_orders");
                return { ...DEFAULT_SECTIONS, own: oldOrders };
            }
        }
    } catch (_) {
        /* ignore */
    }
    return null;
};

const clearLocalStorage = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem("delivery_planner_orders");
    } catch (err) {
        console.error("Failed to clear localStorage:", err);
    }
};

// ─── Due amount ─────────────────────────────────────────────────────────────

/** What's left to collect — "Due ৳80.00", or "Paid" — with the full bill on hover. */
const DueAmount: React.FC<{ order: DeliveryOrder }> = ({ order }) => {
    const due = order.amount_due;
    // An old saved card before the board refreshes: plain bill, as before.
    if (due == null) {
        return (
            <Typography variant="body2" fontWeight={600} color="primary">
                {formatCurrencyBDT(order.total_amount)}
            </Typography>
        );
    }
    const paid = Math.max(0, Number(order.total_amount) - due);
    const detail = `Bill ${formatCurrencyBDT(order.total_amount)} · Paid ${formatCurrencyBDT(paid)}`;
    return (
        <Tooltip title={detail}>
            {due > 0 ? (
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, whiteSpace: "nowrap" }}>
                    <Typography
                        component="span"
                        sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, color: "error.main", textTransform: "uppercase" }}
                    >
                        Due
                    </Typography>
                    <Typography component="span" variant="body2" fontWeight={700} color="error.main">
                        {formatCurrencyBDT(due)}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: "success.main", whiteSpace: "nowrap" }}>
                    <CheckCircle sx={{ fontSize: 16 }} />
                    <Typography component="span" variant="body2" fontWeight={700}>
                        Paid
                    </Typography>
                </Box>
            )}
        </Tooltip>
    );
};

// ─── StatusChip ─────────────────────────────────────────────────────────────

/**
 * The order's status as a chip next to the courier one — click to change it
 * in the same status dialog as the order drawer (stock, Telegram notice and
 * collecting an unpaid balance all work as there).
 */
const StatusChip: React.FC<{ status: Order["status"]; onClick: () => void }> = ({ status, onClick }) => (
    <Tooltip title="Change status">
        <Chip
            icon={getStatusIcon(status)}
            label={status.charAt(0) + status.slice(1).toLowerCase()}
            size="small"
            variant="outlined"
            color={getStatusColor(status) as ChipProps["color"]}
            onClick={onClick}
            onDelete={onClick}
            deleteIcon={<ArrowDropDown />}
            aria-haspopup="dialog"
        />
    </Tooltip>
);

// ─── SortableOrderCard ──────────────────────────────────────────────────────

interface SortableOrderCardProps {
    order: DeliveryOrder;
    index: number;
    sectionKey: SectionKey;
    onMove: (orderId: string, from: SectionKey, to: SectionKey) => void;
    onViewDetails: (orderId: string) => void;
    onUpdateStatus: (orderId: string) => void;
    onRemove?: (orderId: string) => void;
    onOrderChanged: (order: Order) => void;
    notify: PlannerNotify;
}

const SortableOrderCard: React.FC<SortableOrderCardProps> = ({
    order,
    index,
    sectionKey,
    onMove,
    onViewDetails,
    onUpdateStatus,
    onRemove,
    onOrderChanged,
    notify,
}) => {
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: order.order_id });

    // The whole card picks up with a mouse or a long press (see the sensors
    // in DeliverySection); the keyboard uses the grip, so Enter/Space on the
    // card's own buttons still press those buttons.
    const { onKeyDown, ...pointerListeners } = listeners ?? {};
    // dnd-kit types its listeners as plain Functions.
    const onGripKeyDown = onKeyDown as
        | React.KeyboardEventHandler<HTMLDivElement>
        | undefined;

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const targets = MOVE_TARGETS[sectionKey];
    const courier = cardCourier(order);
    const badgeColor =
        sectionKey === "trash"
            ? "text.disabled"
            : SECTION_CONFIGS.find((s) => s.key === sectionKey)?.color ||
              "primary.main";

    return (
        <>
            <Card
                ref={setNodeRef}
                style={style}
                {...pointerListeners}
                sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "stretch",
                    position: "relative",
                    cursor: isDragging ? "grabbing" : "grab",
                    // A drag shouldn't select text or open the long-press menu.
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    WebkitTouchCallout: "none",
                    touchAction: "manipulation",
                    "&:hover": { boxShadow: 3 },
                    ...(isDragging && {
                        zIndex: 2,
                        boxShadow: 8,
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        "&:hover": { boxShadow: 8 },
                    }),
                }}
            >
                {/* Grip: the visual cue, and the keyboard handle */}
                <Box
                    ref={setActivatorNodeRef}
                    {...attributes}
                    onKeyDown={onGripKeyDown}
                    aria-label={`Reorder ${order.customer_name}`}
                    sx={{
                        width: { xs: 32, sm: 36 },
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "action.hover",
                        borderRight: 1,
                        borderColor: "divider",
                        color: isDragging ? "primary.main" : "text.disabled",
                        transition: "color 0.15s",
                        ".MuiCard-root:hover &": { color: "text.secondary" },
                        "&:focus-visible": {
                            outline: "2px solid",
                            outlineColor: "primary.main",
                            outlineOffset: -2,
                        },
                    }}
                >
                    <DragIndicator />
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: { xs: "flex-start", md: "center" },
                        gap: { xs: 1.25, sm: 1.5 },
                        py: 1,
                        px: { xs: 1.25, sm: 2 },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 28,
                            height: 28,
                            mt: { xs: 0.25, md: 0 },
                            borderRadius: "50%",
                            bgcolor: badgeColor,
                            color: "white",
                            fontWeight: "bold",
                            fontSize: 13,
                        }}
                    >
                        {index + 1}
                    </Box>

                    {/* Phones: who & where, then items · total · actions.
                        Wider screens: all on one line. */}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: { md: "center" },
                            gap: { xs: 0.75, md: 2 },
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                <Person sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} />
                                <Typography variant="body2" fontWeight={600} noWrap>
                                    {order.customer_name}
                                </Typography>
                                {order.customer_phone && (
                                    // Tap to call on a phone; the name gives way first when space is short.
                                    <Box
                                        component="a"
                                        href={`tel:${order.customer_phone.replace(/[^\d+]/g, "")}`}
                                        onMouseDown={stopDragStart}
                                        onTouchStart={stopDragStart}
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.25,
                                            ml: 0.75,
                                            flexShrink: 0,
                                            color: "text.secondary",
                                            textDecoration: "none",
                                            fontSize: "0.875rem",
                                            "&:hover": { color: "primary.main", textDecoration: "underline" },
                                        }}
                                    >
                                        <Phone sx={{ fontSize: 14 }} />
                                        {order.customer_phone}
                                    </Box>
                                )}
                            </Box>
                            {order.shipping_address && (
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.25 }}>
                                    <LocationOn
                                        sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0, mt: "2px" }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            minWidth: 0,
                                            display: "-webkit-box",
                                            WebkitBoxOrient: "vertical",
                                            WebkitLineClamp: { xs: 2, md: 1 },
                                            overflow: "hidden",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {order.shipping_address}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Right side: delivery service, then items · total · actions */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: { xs: "stretch", md: "flex-end" },
                                gap: 0.5,
                                flexShrink: 0,
                                minWidth: 0,
                            }}
                        >
                            <Box
                                onMouseDown={stopDragStart}
                                onTouchStart={stopDragStart}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                    justifyContent: { xs: "flex-start", md: "flex-end" },
                                    maxWidth: { md: 380 },
                                    minWidth: 0,
                                    "& .MuiChip-root": { maxWidth: "100%" },
                                }}
                            >
                                {/* Status is for staff, not the delivery list — left out of exported images. */}
                                <Box data-html2canvas-ignore="true" sx={{ display: "flex", flexShrink: 0 }}>
                                    <StatusChip status={order.status} onClick={() => onUpdateStatus(order.order_id)} />
                                </Box>
                                <Box
                                    // An unset "Set courier" chip is left out of exported images.
                                    data-html2canvas-ignore={courier ? undefined : "true"}
                                    sx={{ display: "flex", minWidth: 0, maxWidth: { md: 260 } }}
                                >
                                    <CourierPicker
                                        orderId={order.order_id}
                                        courier={courier}
                                        consignmentId={order.consignment_id}
                                        onOrderChanged={onOrderChanged}
                                        notify={(message, severity) => notify(message, severity, order.order_id)}
                                    />
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1.5,
                                    flexShrink: 0,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Box
                                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                        title={`${order.items_count} item${order.items_count === 1 ? "" : "s"}`}
                                    >
                                        <ShoppingBag sx={{ fontSize: 16, color: "text.secondary" }} />
                                        <Typography variant="body2">{order.items_count}</Typography>
                                    </Box>
                                    <DueAmount order={order} />
                                </Box>

                                <Box sx={{ display: "flex", gap: 0.5 }}>
                                    <Tooltip title="View Details">
                                        <IconButton
                                            size="small"
                                            onClick={() => onViewDetails(order.order_id)}
                                            sx={{ color: "primary.main" }}
                                        >
                                            <InfoOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Move to...">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                                            sx={{ color: "text.secondary" }}
                                        >
                                            <MoveUp fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {sectionKey !== "trash" ? (
                                        <Tooltip title="Move to Trash">
                                            <IconButton
                                                size="small"
                                                onClick={() => onMove(order.order_id, sectionKey, "trash")}
                                                sx={{ color: "error.main" }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <>
                                            <Tooltip title="Restore to Own Deliveries">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onMove(order.order_id, "trash", "own")}
                                                    sx={{ color: "success.main" }}
                                                >
                                                    <RestoreFromTrash fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Remove permanently">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onRemove?.(order.order_id)}
                                                    sx={{ color: "error.main" }}
                                                >
                                                    <DeleteForever fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Card>
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                {targets.map((t) => (
                    <MenuItem
                        key={t.key}
                        onClick={() => {
                            onMove(order.order_id, sectionKey, t.key);
                            setMenuAnchor(null);
                        }}
                    >
                        <ListItemIcon>{t.icon}</ListItemIcon>
                        <ListItemText>{t.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

// ─── DeliverySection ─────────────────────────────────────────────────────────

interface DeliverySectionProps {
    config: SectionConfig;
    orders: DeliveryOrder[];
    onDragEnd: (event: DragEndEvent, sectionKey: SectionKey) => void;
    onMove: (orderId: string, from: SectionKey, to: SectionKey) => void;
    onViewDetails: (orderId: string) => void;
    onUpdateStatus: (orderId: string) => void;
    exporting: boolean;
    onExport: (sectionKey: SectionKey) => void;
    onRemove?: (orderId: string) => void;
    onOrderChanged: (order: Order) => void;
    notify: PlannerNotify;
}

const DeliverySection: React.FC<DeliverySectionProps> = ({
    config,
    orders,
    onDragEnd,
    onMove,
    onViewDetails,
    onUpdateStatus,
    exporting,
    onExport,
    onRemove,
    onOrderChanged,
    notify,
}) => {
    const sectionRef = useRef<HTMLDivElement>(null);

    // Mouse: press anywhere on a card and move a few pixels (a plain click
    // still reaches its buttons). Touch: press and hold briefly, so a normal
    // swipe still scrolls the page. Keyboard: the card's grip.
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 200, tolerance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // What's left to collect across the section, matching the cards.
    const totalDue = orders.reduce((sum, o) => sum + Number(dueOrTotal(o)), 0);

    return (
        <Paper ref={sectionRef} id={`section-${config.key}`} sx={{ mb: 3 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    px: { xs: 1.5, sm: 3 },
                    py: 1.5,
                    bgcolor: config.key === "trash" ? "grey.100" : undefined,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: { xs: 1, sm: 1.5 },
                        minWidth: 0,
                    }}
                >
                    <Box sx={{ color: config.color, display: "flex" }}>
                        {config.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontSize: "1rem", lineHeight: 1.3 }}>
                        {config.label}
                    </Typography>
                    <Chip
                        label={orders.length}
                        size="small"
                        sx={{ fontWeight: "bold" }}
                    />
                    {orders.length > 0 && config.key !== "trash" && (
                        <Tooltip title="Total still to collect in this section">
                            <Chip
                                label={`Due ${formatCurrencyBDT(totalDue)}`}
                                size="small"
                                variant="outlined"
                                color={totalDue > 0 ? "error" : "success"}
                            />
                        </Tooltip>
                    )}
                </Box>
                {orders.length > 0 && config.key !== "trash" && (
                    <Tooltip title={`Export ${config.label} as image`}>
                        <IconButton
                            size="small"
                            onClick={() => onExport(config.key)}
                            disabled={exporting}
                        >
                            <Download fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Divider />

            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                {orders.length === 0 ? (
                    <Box sx={{ py: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            {config.emptyMessage}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                            {config.emptySubMessage}
                        </Typography>
                    </Box>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(e) => onDragEnd(e, config.key)}
                    >
                        <SortableContext
                            items={orders.map((o) => o.order_id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {orders.map((order, idx) => (
                                <SortableOrderCard
                                    key={order.order_id}
                                    order={order}
                                    index={idx}
                                    sectionKey={config.key}
                                    onMove={onMove}
                                    onViewDetails={onViewDetails}
                                    onUpdateStatus={onUpdateStatus}
                                    onRemove={onRemove}
                                    onOrderChanged={onOrderChanged}
                                    notify={notify}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                )}
            </Box>
        </Paper>
    );
};

// ─── DeliveryPlanner (main) ──────────────────────────────────────────────────

export const DeliveryPlanner: React.FC = () => {
    const [sections, setSections] = useState<SectionsState>(DEFAULT_SECTIONS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [exporting, setExporting] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);

    // Order Detail Drawer state
    const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
    const [detailOrder, setDetailOrder] = useState<Order | null>(null);
    const [detailOrderIndex, setDetailOrderIndex] = useState<number>(-1);

    // Short confirmations, e.g. after changing a card's delivery service.
    const [toast, setToast] = useState<{ message: string; severity: "success" | "error" } | null>(null);
    // Cards that just changed section because their courier kind changed,
    // so that card's next message can say where it went.
    const moveNotes = useRef(new Map<string, SectionKey>());
    const notify: PlannerNotify = useCallback((message, severity = "success", orderId) => {
        const movedTo = orderId ? moveNotes.current.get(orderId) : undefined;
        if (orderId) moveNotes.current.delete(orderId);
        setToast({
            message: movedTo ? `${message} — moved to ${SHORT_SECTION_LABEL[movedTo]}` : message,
            severity,
        });
    }, []);

    // ── Persistence ──────────────────────────────────────────────────────

    const persistSections = useCallback((next: SectionsState) => {
        setSections(next);
        saveToLocalStorage(next);
    }, []);

    // ── Keep cards current ───────────────────────────────────────────────

    const sectionsRef = useRef(sections);
    sectionsRef.current = sections;

    const updateCards = useCallback(
        (updates: Map<string, DeliveryOrder | null>) => {
            setSections((prev) => {
                const next = applyCardUpdates(prev, updates);
                if (next !== prev) saveToLocalStorage(next);
                return next;
            });
        },
        [],
    );

    /** A card's order changed here — refresh it, or drop it if finished. */
    const applyOrderToBoard = useCallback(
        (order: Order) => {
            const fresh = mapOrderToDeliveryOrder(order, 0);
            const board = sectionsRef.current;
            const from = SECTION_KEYS.find((k) =>
                board[k].some((c) => c.order_id === fresh.order_id),
            );
            const before = from && board[from].find((c) => c.order_id === fresh.order_id);
            const to = from && before ? sectionAfterChange(from, before, fresh) : from;
            if (to && to !== from && !isFinished(fresh.status)) {
                moveNotes.current.set(fresh.order_id, to);
            } else {
                moveNotes.current.delete(fresh.order_id);
            }
            updateCards(new Map([[order.order_id, fresh]]));
        },
        [updateCards],
    );

    /**
     * Bring the cards on `board` up to date. `listed` holds the current
     * Pending/Confirmed orders; cards not among them changed status or were
     * deleted since they were added, so those are fetched individually. A
     * card is only removed when its order is finished or gone (404) — a
     * failed request leaves it as it is.
     */
    const refreshCards = useCallback(
        async (board: SectionsState, listed: DeliveryOrder[]) => {
            const listedById = new Map(listed.map((o) => [o.order_id, o]));
            const updates = new Map<string, DeliveryOrder | null>();
            const unlisted: string[] = [];
            boardIds(board).forEach((id) => {
                const fresh = listedById.get(id);
                if (fresh) updates.set(id, fresh);
                else unlisted.push(id);
            });

            for (let i = 0; i < unlisted.length; i += REFRESH_BATCH_SIZE) {
                const batch = unlisted.slice(i, i + REFRESH_BATCH_SIZE);
                const results = await Promise.all(
                    batch.map((id) =>
                        orderService.getOrderById(id).then(
                            (order) => mapOrderToDeliveryOrder(order, 0),
                            (err) =>
                                err?.response?.status === 404 ? null : undefined,
                        ),
                    ),
                );
                batch.forEach((id, j) => {
                    if (results[j] !== undefined) updates.set(id, results[j]!);
                });
            }

            updateCards(updates);
        },
        [updateCards],
    );

    /** A new order made here (e.g. a clone) joins the board like an import. */
    const addCreatedOrder = useCallback(async (created: Order) => {
        if (!created?.order_id) return;
        let order = created;
        try {
            order = await orderService.getOrderById(created.order_id);
        } catch {
            // use what the form returned
        }
        if (order.status !== "PENDING" && order.status !== "CONFIRMED") return;
        const card = mapOrderToDeliveryOrder(order, 0);
        setSections((prev) => {
            if (boardIds(prev).has(card.order_id)) return prev;
            const next = addNewCards(prev, [card]);
            saveToLocalStorage(next);
            return next;
        });
    }, []);

    /** Changed from a card (e.g. its delivery service): update it, and the drawer if it's showing it. */
    const handleCardOrderChanged = useCallback(
        (fresh: Order) => {
            setDetailOrder((current) =>
                current?.order_id === fresh.order_id ? fresh : current,
            );
            applyOrderToBoard(fresh);
        },
        [applyOrderToBoard],
    );

    // Receipt, edit, clone, status, payment and courier — the same actions
    // and dialogs as the All Orders drawer.
    const orderActions = useOrderActions({
        onOrderUpdated: (fresh) => {
            setDetailOrder((current) =>
                current?.order_id === fresh.order_id ? fresh : current,
            );
            applyOrderToBoard(fresh);
        },
        onOrderCreated: (created) => void addCreatedOrder(created),
        onError: setError,
    });

    // ── Fetch from API ───────────────────────────────────────────────────

    const fetchOrdersFromApi = useCallback(async (): Promise<
        DeliveryOrder[]
    > => {
        const [pendingResponse, confirmedResponse] = await Promise.all([
            orderService.getOrders({ status: "PENDING", limit: 100 }),
            orderService.getOrders({ status: "CONFIRMED", limit: 100 }),
        ]);

        const allOrders = [
            ...pendingResponse.items,
            ...confirmedResponse.items,
        ];

        return allOrders.map((order, index) =>
            mapOrderToDeliveryOrder(order, index),
        );
    }, []);

    const fetchDeliveryOrders = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const deliveryOrders = await fetchOrdersFromApi();

            const ownOrders = deliveryOrders.filter(
                (o) => o.delivery_service_type !== "THIRD_PARTY",
            );
            const homeOrders = deliveryOrders.filter(
                (o) => o.delivery_service_type === "THIRD_PARTY",
            );
            const newSections: SectionsState = {
                own: reindex(ownOrders),
                home: reindex(homeOrders),
                point: [],
                trash: [],
            };
            persistSections(newSections);
        } catch (err: any) {
            console.error("Failed to fetch delivery orders:", err);
            setError("Failed to load delivery orders. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [fetchOrdersFromApi, persistSections]);

    // ── Init ─────────────────────────────────────────────────────────────

    useEffect(() => {
        const stored = loadFromLocalStorage();
        if (stored) {
            const hasAnyOrders =
                stored.own.length > 0 ||
                stored.home.length > 0 ||
                stored.point.length > 0 ||
                stored.trash.length > 0;
            if (hasAnyOrders) {
                setSections(stored);
                setLoading(false);
                // Saved cards are a snapshot — quietly bring them up to date.
                fetchOrdersFromApi()
                    .then((listed) => refreshCards(stored, listed))
                    .catch(() => {
                        /* keep the saved cards */
                    });
                return;
            }
        }
        fetchDeliveryOrders();
    }, [fetchDeliveryOrders, fetchOrdersFromApi, refreshCards]);

    // ── Drag & Drop ──────────────────────────────────────────────────────

    const handleDragEnd = useCallback(
        (event: DragEndEvent, sectionKey: SectionKey) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            setSections((prev) => {
                const list = [...prev[sectionKey]];
                const oldIndex = list.findIndex(
                    (o) => o.order_id === active.id,
                );
                const newIndex = list.findIndex((o) => o.order_id === over.id);
                if (oldIndex === -1 || newIndex === -1) return prev;

                const next = {
                    ...prev,
                    [sectionKey]: reindex(arrayMove(list, oldIndex, newIndex)),
                };
                saveToLocalStorage(next);
                return next;
            });
        },
        [],
    );

    // ── Move between sections ────────────────────────────────────────────

    const handleMove = useCallback(
        (orderId: string, from: SectionKey, to: SectionKey) => {
            if (from === to) return;

            setSections((prev) => {
                const order = prev[from].find((o) => o.order_id === orderId);
                if (!order) return prev;

                const next: SectionsState = {
                    ...prev,
                    [from]: reindex(
                        prev[from].filter((o) => o.order_id !== orderId),
                    ),
                    [to]: reindex([...prev[to], order]),
                };
                saveToLocalStorage(next);
                return next;
            });
        },
        [],
    );

    // ── Flattened orders list for drawer navigation ─────────────────────

    const allOrdersForNavigation = useMemo(() => {
        // Combine all sections into a single list for navigation
        // Order: own, home, point, trash
        return [
            ...sections.own,
            ...sections.home,
            ...sections.point,
            ...sections.trash,
        ];
    }, [sections]);

    // Cards move (drag, move) or drop off (delivered, cancelled) while the
    // drawer is open. On the board, follow the order's position; off it,
    // keep its old slot so "next" is the card that took its place.
    const detailOrderId = detailOrder?.order_id;
    const detailOnBoard =
        !!detailOrderId &&
        allOrdersForNavigation.some((o) => o.order_id === detailOrderId);

    useEffect(() => {
        if (!detailOrderId) return;
        const idx = allOrdersForNavigation.findIndex(
            (o) => o.order_id === detailOrderId,
        );
        if (idx !== -1) setDetailOrderIndex(idx);
    }, [allOrdersForNavigation, detailOrderId]);

    // ── View Details ─────────────────────────────────────────────────────

    const handleViewDetails = useCallback(
        async (orderId: string) => {
            try {
                setDetailsLoading(true);
                // Find index in the flattened list for navigation
                const idx = allOrdersForNavigation.findIndex(
                    (o) => o.order_id === orderId,
                );
                const detailedOrder = await orderService.getOrderById(orderId);
                setDetailOrder(detailedOrder);
                setDetailOrderIndex(idx);
                setDetailDrawerOpen(true);
            } catch (err: any) {
                console.error("Failed to load order details:", err);
                setError("Failed to load order details. Please try again.");
            } finally {
                setDetailsLoading(false);
            }
        },
        [allOrdersForNavigation],
    );

    // ── Navigate Details ───────────────────────────────────────────────────

    const handleNavigateDetail = useCallback(
        async (direction: "prev" | "next") => {
            const nextIndex =
                direction === "prev"
                    ? detailOrderIndex - 1
                    : detailOnBoard
                      ? detailOrderIndex + 1
                      : detailOrderIndex;
            if (nextIndex < 0 || nextIndex >= allOrdersForNavigation.length)
                return;

            const targetOrder = allOrdersForNavigation[nextIndex];
            setDetailOrderIndex(nextIndex);
            // Set basic order data immediately for UI responsiveness
            setDetailOrder(targetOrder as unknown as Order);

            try {
                // Fetch full order details
                const full = await orderService.getOrderById(
                    targetOrder.order_id,
                );
                setDetailOrder(full);
            } catch {
                // Already showing list-level data; silently ignore
            }
        },
        [detailOrderIndex, detailOnBoard, allOrdersForNavigation],
    );

    // ── Import New Orders ──────────────────────────────────────────────

    const handleImportNewOrders = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const apiOrders = await fetchOrdersFromApi();
            const board = sectionsRef.current;

            setSections((prev) => {
                // Collect all existing order IDs across all sections
                const existingIds = new Set<string>([
                    ...prev.own.map((o) => o.order_id),
                    ...prev.home.map((o) => o.order_id),
                    ...prev.point.map((o) => o.order_id),
                    ...prev.trash.map((o) => o.order_id),
                ]);

                // Filter to only truly new orders
                const newOrders = apiOrders.filter(
                    (o) => !existingIds.has(o.order_id),
                );

                if (newOrders.length === 0) {
                    setError("");
                    return prev;
                }

                const next = addNewCards(prev, newOrders);
                saveToLocalStorage(next);
                return next;
            });

            // Cards already on the board may have changed since.
            await refreshCards(board, apiOrders);
        } catch (err: any) {
            console.error("Failed to import new orders:", err);
            setError("Failed to import new orders. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [fetchOrdersFromApi, refreshCards]);

    // ── Remove order permanently ──────────────────────────────────────────

    const handleRemoveOrder = useCallback((orderId: string) => {
        setSections((prev) => {
            const next: SectionsState = {
                ...prev,
                trash: reindex(
                    prev.trash.filter((o) => o.order_id !== orderId),
                ),
            };
            saveToLocalStorage(next);
            return next;
        });
    }, []);

    // ── Reset ─────────────────────────────────────────────────────────────

    const handleReset = useCallback(async () => {
        clearLocalStorage();
        await fetchDeliveryOrders();
    }, [fetchDeliveryOrders]);

    // ── Export Screenshot per section ────────────────────────────────────

    const handleExportScreenshot = useCallback(
        async (sectionKey: SectionKey) => {
            const el = document.getElementById(`section-${sectionKey}`);
            if (!el) return;

            try {
                setExporting(true);
                const config = SECTION_CONFIGS.find(
                    (s) => s.key === sectionKey,
                );
                const label = config
                    ? config.label.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()
                    : sectionKey;

                const canvas = await html2canvas(el, {
                    backgroundColor: "#ffffff",
                    scale: 2,
                    logging: false,
                });

                const link = document.createElement("a");
                link.download = `delivery-${label}-${new Date().toISOString().split("T")[0]}.png`;
                link.href = canvas.toDataURL("image/png");
                link.click();
            } catch (err) {
                console.error("Failed to export screenshot:", err);
                setError("Failed to export screenshot. Please try again.");
            } finally {
                setExporting(false);
            }
        },
        [],
    );

    // ── Totals ───────────────────────────────────────────────────────────

    const totalOrders =
        sections.own.length + sections.home.length + sections.point.length;

    // ── Render ───────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "60vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 } }}>
            {/* Header */}
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <LocalShipping
                        sx={{
                            fontSize: { xs: 28, sm: 36 },
                            mr: { xs: 1, sm: 1.5 },
                            color: "primary.main",
                        }}
                    />
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontSize: { xs: "1.6rem", sm: "2.125rem" } }}
                    >
                        Delivery Planner
                    </Typography>
                </Box>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                >
                    Plan and prioritize upcoming deliveries. Drag a card to
                    reorder it (on a phone, press and hold first). Use the
                    move button to switch orders between sections.
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                        icon={<LocalShipping sx={{ fontSize: 16 }} />}
                        label={`Own: ${sections.own.length}`}
                        color="primary"
                        size="small"
                    />
                    <Chip
                        icon={<Home sx={{ fontSize: 16 }} />}
                        label={`Home: ${sections.home.length}`}
                        size="small"
                        sx={{
                            bgcolor: "#e65100",
                            color: "white",
                            "& .MuiChip-icon": { color: "white" },
                        }}
                    />
                    <Chip
                        icon={<Business sx={{ fontSize: 16 }} />}
                        label={`Point: ${sections.point.length}`}
                        size="small"
                        sx={{
                            bgcolor: "#1565c0",
                            color: "white",
                            "& .MuiChip-icon": { color: "white" },
                        }}
                    />
                    <Chip
                        icon={<DeleteOutline sx={{ fontSize: 16 }} />}
                        label={`Trash: ${sections.trash.length}`}
                        size="small"
                        variant="outlined"
                    />
                    <Chip
                        label={`Total Active: ${totalOrders}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                    />
                </Box>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError("")}
                >
                    {error}
                </Alert>
            )}

            {/* Actions */}
            <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, mb: 3 }}>
                <Button
                    variant="contained"
                    startIcon={<AddCircleOutline />}
                    onClick={handleImportNewOrders}
                    disabled={loading}
                    sx={{ flex: { xs: 2, sm: "none" }, whiteSpace: "nowrap" }}
                >
                    Import New Orders
                </Button>
                <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RestartAlt />}
                    onClick={handleReset}
                    disabled={loading}
                    sx={{ flex: { xs: 1, sm: "none" } }}
                >
                    Reset
                </Button>
            </Box>

            {/* Sections */}
            {SECTION_CONFIGS.map((config) => (
                <DeliverySection
                    key={config.key}
                    config={config}
                    orders={sections[config.key]}
                    onDragEnd={handleDragEnd}
                    onMove={handleMove}
                    onViewDetails={handleViewDetails}
                    onUpdateStatus={orderActions.updateStatus}
                    exporting={exporting}
                    onExport={handleExportScreenshot}
                    onRemove={
                        config.key === "trash" ? handleRemoveOrder : undefined
                    }
                    onOrderChanged={handleCardOrderChanged}
                    notify={notify}
                />
            ))}

            {/* Loading overlay for details fetch */}
            {detailsLoading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: "rgba(255,255,255,0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {/* Order Detail Drawer */}
            <OrderDetailDrawer
                open={detailDrawerOpen}
                onClose={() => setDetailDrawerOpen(false)}
                order={detailOrder}
                onNavigate={handleNavigateDetail}
                hasPrev={detailOrderIndex > 0}
                hasNext={
                    detailOrderIndex <
                    allOrdersForNavigation.length - (detailOnBoard ? 1 : 0)
                }
                currentIndex={
                    detailOnBoard && detailOrderIndex >= 0
                        ? detailOrderIndex
                        : undefined
                }
                totalCount={allOrdersForNavigation.length}
                {...orderActions.drawerActionProps}
                onPaymentAdded={(updatedOrder) => {
                    setDetailOrder(updatedOrder);
                    applyOrderToBoard(updatedOrder);
                }}
                onDiscarded={(order) =>
                    updateCards(new Map([[order.order_id, null]]))
                }
            />
            {orderActions.dialogs}

            <Snackbar
                open={!!toast}
                autoHideDuration={4000}
                onClose={(_, reason) => reason !== "clickaway" && setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={toast?.severity ?? "success"} variant="filled" onClose={() => setToast(null)}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};
